import { useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  ExperimentOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import NumberWithUnit from '@/shared/components/NumberWithUnit'
import { millLotApi } from '@/features/baleManagement/api'

const { Text, Title } = Typography
const API = ''

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PENDING_APPROVAL: 'processing',
  APPROVED: 'success',
  ISSUED: 'blue',
  IN_PRODUCTION: 'purple',
  COMPLETED: 'cyan',
  CANCELLED: 'error',
}

function asNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function formatNumber(value: unknown, digits = 2, suffix = ''): string {
  const num = asNumber(value)
  if (num === null) return '-'
  return `${num.toLocaleString('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}${suffix}`
}

function PredictionStrip({ prediction }: { prediction?: Record<string, any> | null }) {
  if (!prediction) return null

  const cards = [
    {
      label: 'Expected CSP',
      value: prediction.expected_csp,
      secondary: prediction.expected_csp_low && prediction.expected_csp_high
        ? `${formatNumber(prediction.expected_csp_low, 0)} to ${formatNumber(prediction.expected_csp_high, 0)}`
        : null,
    },
    {
      label: 'Expected Waste',
      value: prediction.expected_waste_pct,
      secondary: prediction.expected_waste_low && prediction.expected_waste_high
        ? `${formatNumber(prediction.expected_waste_low, 2)}% to ${formatNumber(prediction.expected_waste_high, 2)}%`
        : null,
      suffix: '%',
    },
    {
      label: 'Expected Realization',
      value: prediction.expected_realization_pct,
      secondary: prediction.expected_realization_low && prediction.expected_realization_high
        ? `${formatNumber(prediction.expected_realization_low, 2)}% to ${formatNumber(prediction.expected_realization_high, 2)}%`
        : null,
      suffix: '%',
    },
    {
      label: 'Confidence',
      value: prediction.confidence_pct,
      secondary: prediction.history_matches != null
        ? `${prediction.confidence_label || 'LOW'} confidence, ${prediction.history_matches} history matches`
        : prediction.confidence_label || null,
      suffix: '%',
    },
  ]

  return (
    <Row gutter={12} style={{ marginBottom: 12 }}>
      {cards.map((card) => (
        <Col span={6} key={card.label}>
          <Card size="small">
            <Text type="secondary" style={{ fontSize: 12 }}>{card.label}</Text>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {formatNumber(card.value, card.label === 'Expected CSP' ? 0 : 2, card.suffix || '')}
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c', minHeight: 18 }}>
              {card.secondary || '\u00A0'}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  )
}

export default function MixingPlanPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()

  const { data: plans, isLoading } = useQuery({
    queryKey: ['mixing-plans', plantId],
    queryFn: () =>
      apiClient.get(`${API}/mixing-plans`, { params: { plant_id: plantId } }).then((r) => r.data),
    enabled: !!plantId,
  })

  const { data: millLotsData } = useQuery({
    queryKey: ['mixing-plan-mill-lots', plantId],
    queryFn: () => millLotApi.list({ plant_id: plantId, limit: 500 }),
    enabled: !!plantId,
  })

  const createMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => apiClient.post(`${API}/mixing-plans`, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mixing-plans'] })
      message.success('Mixing plan created')
      setOpen(false)
      form.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed to create mixing plan'),
  })

  const approveMut = useMutation({
    mutationFn: (id: string) => apiClient.post(`${API}/mixing-plans/${id}/approve`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mixing-plans'] })
      message.success('Mixing plan approved')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed to approve'),
  })

  const millLots = Array.isArray(millLotsData) ? millLotsData : []
  const availableLots = useMemo(
    () => millLots.filter((lot: any) => Number(lot.available_weight_kg || 0) > 0),
    [millLots],
  )
  const lotMap = useMemo(
    () => new Map(availableLots.map((lot: any) => [lot.id, lot])),
    [availableLots],
  )
  const lotOptions = useMemo(
    () => availableLots.map((lot: any) => ({
      value: lot.id,
      label: `${lot.mill_lot_no} | ${lot.supplier_name || 'Supplier'} | ${formatNumber(lot.available_weight_kg, 0, ' kg')}`,
    })),
    [availableLots],
  )

  const watchedLines = Form.useWatch('lines', form) || []
  const watchedTotalWeight = asNumber(Form.useWatch('total_weight_kg', form)) || 0
  const proportionTotal = watchedLines.reduce(
    (sum: number, line: any) => sum + (asNumber(line?.proportion_pct) || 0),
    0,
  )

  const linePreview = watchedLines.map((line: any) => {
    const lot = lotMap.get(line?.mill_lot_id)
    const proportion = asNumber(line?.proportion_pct) || 0
    const computedWeight = watchedTotalWeight > 0 ? (watchedTotalWeight * proportion) / 100 : 0
    return { lot, proportion, computedWeight }
  })

  const handleSubmit = async () => {
    const vals = await form.validateFields()
    const cleanLines = (vals.lines || []).filter((line: any) => line?.mill_lot_id && asNumber(line?.proportion_pct) !== null)

    if (!cleanLines.length) {
      message.error('Add at least one mill lot')
      return
    }
    if (Math.abs(proportionTotal - 100) > 0.05) {
      message.error('Total proportion must be 100%')
      return
    }

    createMut.mutate({
      plant_id: plantId,
      plan_date: vals.plan_date?.format('YYYY-MM-DD'),
      count_ne: vals.count_ne,
      count_description: vals.count_description,
      total_weight_kg: vals.total_weight_kg,
      start_date: vals.start_date?.format('YYYY-MM-DD'),
      remarks: vals.remarks,
      lines: cleanLines.map((line: any) => ({
        mill_lot_id: line.mill_lot_id,
        proportion_pct: line.proportion_pct,
      })),
    })
  }

  const columns = [
    {
      title: 'MX No',
      dataIndex: 'mx_no',
      key: 'mx_no',
      width: 140,
      render: (value: string) => <b style={{ fontFamily: 'monospace' }}>{value}</b>,
    },
    {
      title: 'Plan Date',
      dataIndex: 'plan_date',
      key: 'plan_date',
      width: 120,
      render: (value: string) => dayjs(value).format('DD-MMM-YYYY'),
    },
    {
      title: 'Count',
      dataIndex: 'count_description',
      key: 'count_description',
      render: (_: unknown, record: any) => record.count_description || (record.count_ne ? `Ne ${record.count_ne}` : '-'),
    },
    {
      title: 'Total (kg)',
      dataIndex: 'total_weight_kg',
      key: 'total_weight_kg',
      width: 120,
      align: 'right' as const,
      render: (value: number) => formatNumber(value, 3),
    },
    {
      title: 'Expected CSP',
      key: 'expected_csp',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: any) => formatNumber(record.prediction_json?.expected_csp, 0),
    },
    {
      title: 'Realization',
      key: 'expected_realization_pct',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: any) => formatNumber(record.prediction_json?.expected_realization_pct, 2, '%'),
    },
    {
      title: 'Confidence',
      key: 'confidence_pct',
      width: 120,
      render: (_: unknown, record: any) => (
        record.prediction_json?.confidence_pct != null ? (
          <Space direction="vertical" size={0}>
            <Text strong>{formatNumber(record.prediction_json?.confidence_pct, 0, '%')}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.prediction_json?.confidence_label || 'LOW'}
            </Text>
          </Space>
        ) : '-'
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (value: string) => <Tag color={STATUS_COLORS[value] ?? 'default'}>{value?.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_: unknown, record: any) => (
        <Space>
          {record.status === 'DRAFT' && (
            <Popconfirm title="Approve this mixing plan?" onConfirm={() => approveMut.mutate(record.id)}>
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                Approve
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ExperimentOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Mixing Plans
      </Title>
      <Typography.Text type="secondary">
        Choose the mill lots and mixing percentage. Supplier, HVI, cost, expected CSP, waste, and realization are pulled from the ERP automatically.
      </Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row justify="space-between" style={{ marginBottom: 16 }}>
          <Select
            value={plantId}
            onChange={setPlantId}
            style={{ width: 220 }}
            options={plants.map((plant: any) => ({ value: plant.id, label: plant.name ?? plant.plant_name }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields()
              form.setFieldsValue({ plan_date: dayjs(), lines: [{}] })
              setOpen(true)
            }}
          >
            New Mixing Plan
          </Button>
        </Row>

        <Table
          dataSource={Array.isArray(plans) ? plans : []}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20 }}
          expandable={{
            expandedRowRender: (record: any) => (
              <>
                <PredictionStrip prediction={record.prediction_json} />
                {(record.prediction_json?.warnings || []).length > 0 && (
                  <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    message="Prediction warnings"
                    description={(record.prediction_json?.warnings || []).join(' | ')}
                  />
                )}
                <Table
                  dataSource={record.lines || []}
                  size="small"
                  pagination={false}
                  rowKey="id"
                  columns={[
                    { title: 'Mill Lot', dataIndex: 'lot_no', key: 'lot_no', render: (value: string) => <b>{value}</b> },
                    { title: 'Grade', dataIndex: 'grade', key: 'grade', width: 90 },
                    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true },
                    { title: 'Avg Mic', dataIndex: 'avg_micronaire', key: 'avg_micronaire', align: 'right' as const, render: (value: number) => formatNumber(value, 2) },
                    { title: 'Avg UHML', dataIndex: 'avg_staple_mm', key: 'avg_staple_mm', align: 'right' as const, render: (value: number) => formatNumber(value, 2, ' mm') },
                    { title: 'Mix %', dataIndex: 'proportion_pct', key: 'proportion_pct', align: 'right' as const, render: (value: number) => formatNumber(value, 1, '%') },
                    { title: 'Weight', dataIndex: 'weight_kg', key: 'weight_kg', align: 'right' as const, render: (value: number) => formatNumber(value, 3, ' kg') },
                    { title: 'Bales', dataIndex: 'bale_count', key: 'bale_count', align: 'right' as const },
                    { title: 'Cost/kg', dataIndex: 'cost_per_kg', key: 'cost_per_kg', align: 'right' as const, render: (value: number) => `Rs ${formatNumber(value, 4)}` },
                  ]}
                />
              </>
            ),
          }}
        />
      </Card>

      <Drawer
        title="New Mixing Plan"
        open={open}
        onClose={() => setOpen(false)}
        width={900}
        footer={
          <Row justify="space-between" align="middle">
            <Text type={Math.abs(proportionTotal - 100) > 0.05 ? 'danger' : 'secondary'}>
              Total proportion: {formatNumber(proportionTotal, 1, '%')}
            </Text>
            <Space>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="primary" loading={createMut.isPending} onClick={handleSubmit}>
                Save Plan
              </Button>
            </Space>
          </Row>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Low-entry workflow"
          description="Only choose the count, total mix weight, mill lots, and proportion. The ERP pulls supplier, bale count, HVI averages, cost, and prediction inputs automatically."
        />

        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="plan_date" label="Plan Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="count_ne" label="Count (Ne)">
                <NumberWithUnit unit="Ne" style={{ width: '100%' }} precision={1} min={0} placeholder="30" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="total_weight_kg" label="Total Mixing Weight" rules={[{ required: true }]}>
                <NumberWithUnit unit="kg" style={{ width: '100%' }} precision={3} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="count_description" label="Count Description">
                <Input placeholder="30s Carded Compact Weaving" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="start_date" label="Start Date">
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="remarks" label="Remarks">
                <Input placeholder="Any note for production" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Mill Lot Mix</Divider>
          <Form.List name="lines">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => {
                  const preview = linePreview[name]
                  const lot = preview?.lot
                  return (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 12 }}
                      extra={
                        fields.length > 1 ? (
                          <Button size="small" danger onClick={() => remove(name)}>
                            Remove
                          </Button>
                        ) : null
                      }
                    >
                      <Row gutter={12}>
                        <Col span={11}>
                          <Form.Item name={[name, 'mill_lot_id']} label="Mill Lot" rules={[{ required: true }]}>
                            <Select
                              showSearch
                              optionFilterProp="label"
                              placeholder="Select mill lot"
                              options={lotOptions}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item name={[name, 'proportion_pct']} label="Mix %" rules={[{ required: true }]}>
                            <NumberWithUnit unit="%" style={{ width: '100%' }} precision={1} min={0} max={100} />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <div style={{ paddingTop: 30 }}>
                            <Text type="secondary">Weight</Text>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>
                              {formatNumber(preview?.computedWeight, 3, ' kg')}
                            </div>
                          </div>
                        </Col>
                        <Col span={4}>
                          <div style={{ paddingTop: 30 }}>
                            <Text type="secondary">Available</Text>
                            <div style={{ fontSize: 18, fontWeight: 700 }}>
                              {formatNumber(lot?.available_weight_kg, 0, ' kg')}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row gutter={12}>
                        <Col span={6}>
                          <Text type="secondary">Supplier</Text>
                          <div>{lot?.supplier_name || '-'}</div>
                        </Col>
                        <Col span={4}>
                          <Text type="secondary">Grade</Text>
                          <div>{lot?.grade || '-'}</div>
                        </Col>
                        <Col span={4}>
                          <Text type="secondary">Approved Bales</Text>
                          <div>{lot?.approved_bales ?? '-'}</div>
                        </Col>
                        <Col span={3}>
                          <Text type="secondary">Mic</Text>
                          <div>{formatNumber(lot?.avg_micronaire, 2)}</div>
                        </Col>
                        <Col span={3}>
                          <Text type="secondary">UHML</Text>
                          <div>{formatNumber(lot?.avg_uhml_mm, 2)}</div>
                        </Col>
                        <Col span={4}>
                          <Text type="secondary">Cost/kg</Text>
                          <div>{lot?.landed_cost_per_kg != null ? `Rs ${formatNumber(lot?.landed_cost_per_kg, 4)}` : '-'}</div>
                        </Col>
                      </Row>
                    </Card>
                  )
                })}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} block>
                  Add Mill Lot
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </div>
  )
}
